// challenge-panel-resize — the two reading panels on the Code Challenge surface can be resized
// (Trello LiwLkRvL + JBfO8Ilp: "the list is small and hard to work with"):
//
//   * the "What to add" requirements drawer (`#challenge-drawer`) — WIDTH, by a vertical
//     separator on the panel's CODE-facing left edge (`#challenge-drawer-resize`); the drawer
//     is anchored to the pane's right edge, so dragging LEFT widens it;
//   * the instructions banner (`#instructions-banner`) — HEIGHT, by a horizontal separator
//     under its body (`#instructions-resize`).
//
// Both separators are pointer-draggable AND keyboard-operable (role="separator", arrow keys,
// Home/End, Enter/Space or a double-click to go back to the default size), and each chosen size
// is remembered per viewer in localStorage. Free-form drag-and-drop REORDERING of the panels is
// deliberately not offered — resize plus the larger default type is the whole change.
//
// Modelled on console-resize.js (the existing splitter): the same ship-hidden-then-unhide rule
// for the separators, the same "only an explicit user action persists, never a layout clamp"
// rule, and the same throw-safe storage. It is an ALLOW-LISTED sibling (like plan-chrome.js):
// a no-op unless `body[data-surface="challenge"]`, and null-safe on missing markup.
//
// Wired from main.js in two parts, like console-resize:
//   applyStoredChallengePanels(doc)   EAGER — replays the stored sizes before the payload
//                                     round-trip, so neither panel snaps on load.
//   wireChallengePanelResize(core)    LATE — the separators' interactive behaviour; `core` is
//                                     only used to call getEditor().resize() after the banner
//                                     changes the code pane's height.
//
// MUST NEVER THROW: both entry points run inside main.js, where a throw would skip the rest
// of the editor's wiring.

export var DRAWER_KEY = 'pc.challenge.drawer-width';
export var BANNER_KEY = 'pc.challenge.instructions-height';

// The drawer is an OVERLAY on the code pane, so its width never shrinks the editor — but it
// covers it. The maximum always leaves CODE_VISIBLE_PX of code showing, PLUS the strip the
// editor is inset by so Ace's scrollbar stays clear of the peek tab (DRAWER_TAB_PX).
export var DRAWER_DEFAULT_PX = 360;
export var DRAWER_MIN_PX = 260;
export var DRAWER_MAX_PX = 640;
export var CODE_VISIBLE_PX = 160;
// The peek tab's footprint, mirroring `--drawer-tab-w` in lib/views/embed/challenge.html (the
// two are pinned equal by a source scan). A2b: the editor is inset by this so Ace's vertical
// scrollbar never sits under the tab, and drawerBounds subtracts it so the code-visible floor
// still holds at DRAWER_MAX_PX.
export var DRAWER_TAB_PX = 40;

// The banner DOES take height from the code + stage split below it. It may always be as tall
// as today's default cap (34vh — the CSS max-height), and taller only while the split keeps
// at least SPLIT_FLOOR_PX / SPLIT_FLOOR_VH. Its ceiling is also its content height: dragging
// past the end of the text would only add empty space.
export var BANNER_MIN_PX = 72;
export var BANNER_DEFAULT_VH = 0.34;
export var BANNER_MAX_VH = 0.6;
export var SPLIT_FLOOR_PX = 240;
export var SPLIT_FLOOR_VH = 0.35;

export var KEY_STEP_PX = 24;

// ---------------------------------------------------------------------------
// Pure bounds maths (unit-tested by test/editor/challenge-panel-resize.test.js)
// ---------------------------------------------------------------------------

function num(v) {
  return (typeof v === 'number' && isFinite(v)) ? v : 0;
}

// { min, max } for the drawer, from the code pane's width. The inset tab strip (DRAWER_TAB_PX)
// is deducted alongside the code-visible floor. Returns null when the pane has no usable width
// (not laid out yet), so the caller leaves the CSS default alone.
export function drawerBounds(paneWidth) {
  var pane = num(paneWidth);
  if (pane <= 0) { return null; }
  var max = Math.min(DRAWER_MAX_PX, pane - CODE_VISIBLE_PX - DRAWER_TAB_PX);
  if (max <= 0) { return null; }
  var min = Math.min(DRAWER_MIN_PX, max);
  return { min: min, max: max };
}

export function clampDrawerWidth(input) {
  input = input || {};
  var b = drawerBounds(input.paneWidth);
  if (!b) { return null; }
  var desired = num(input.desired) > 0 ? num(input.desired) : DRAWER_DEFAULT_PX;
  return Math.round(Math.min(Math.max(desired, b.min), b.max));
}

// { min, max } for the banner body. `current` is the body's rendered height now and `split`
// the code + stage split's height now, so `current + split - floor` is the tallest the body
// can be while the split keeps its floor.
export function bannerBounds(input) {
  input = input || {};
  var viewport = num(input.viewport);
  var content = num(input.content);
  if (viewport <= 0 || content <= 0) { return null; }
  var floor = Math.max(SPLIT_FLOOR_PX, viewport * SPLIT_FLOOR_VH);
  var roomy = num(input.current) + num(input.split) - floor;
  var max = Math.min(viewport * BANNER_MAX_VH, content, Math.max(viewport * BANNER_DEFAULT_VH, roomy));
  var min = Math.min(BANNER_MIN_PX, content);
  if (max < min) { max = min; }
  return { min: min, max: max };
}

export function clampBannerHeight(input) {
  var b = bannerBounds(input);
  if (!b || num(input.desired) <= 0) { return null; }
  return Math.round(Math.min(Math.max(num(input.desired), b.min), b.max));
}

// A stored size must be a positive whole-ish number of pixels below an absurd ceiling;
// anything else (garbage, negative, hostile) is ignored.
export function parseStoredPx(raw) {
  if (typeof raw !== 'string' || !raw) { return null; }
  var value = parseFloat(raw);
  if (!isFinite(value) || value <= 0 || value > 10000) { return null; }
  return Math.round(value);
}

// The keyboard step: which way a key moves the size, or a named action. `axis` is 'x' for the
// drawer (LEFT grows — its handle sits on the code-facing left edge) and 'y' for the banner
// (Down grows — the handle sits under the text).
export function keyAction(key, axis) {
  if (key === 'Enter' || key === ' ' || key === 'Spacebar') { return 'reset'; }
  if (key === 'Home') { return 'min'; }
  if (key === 'End') { return 'max'; }
  if (axis === 'x') {
    if (key === 'ArrowRight' || key === 'Right') { return 1; }
    if (key === 'ArrowLeft' || key === 'Left') { return -1; }
  } else {
    if (key === 'ArrowDown' || key === 'Down') { return 1; }
    if (key === 'ArrowUp' || key === 'Up') { return -1; }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Storage (throw-safe end to end — private mode / quota / a blocked policy all throw)
// ---------------------------------------------------------------------------

function store(doc) {
  try {
    var view = doc && doc.defaultView;
    return (view && view.localStorage) || null;
  } catch (e) { return null; }
}
export function readStoredPx(doc, key) {
  try {
    var s = store(doc);
    return s ? parseStoredPx(s.getItem(key)) : null;
  } catch (e) { return null; }
}
function writeStoredPx(doc, key, px) {
  try { var s = store(doc); if (s) { s.setItem(key, String(Math.round(px))); } } catch (e) { /* session-only */ }
}
function clearStored(doc, key) {
  try { var s = store(doc); if (s) { s.removeItem(key); } } catch (e) { /* nothing to do */ }
}

// ---------------------------------------------------------------------------
// The two panels
// ---------------------------------------------------------------------------

function isChallenge(doc) {
  try { return !!(doc && doc.body && doc.body.getAttribute('data-surface') === 'challenge'); } catch (e) { return false; }
}

function height(el) {
  try { return el ? el.getBoundingClientRect().height : 0; } catch (e) { return 0; }
}
function width(el) {
  try { return el ? el.getBoundingClientRect().width : 0; } catch (e) { return 0; }
}

// Each panel is described by the same small interface, so ONE separator wiring serves both.
function drawerPanel(doc) {
  var drawer = doc.getElementById('challenge-drawer');
  var handle = doc.getElementById('challenge-drawer-resize');
  if (!drawer) { return null; }
  var pane = (drawer.closest && drawer.closest('.editor-pane')) || drawer.parentNode;
  var panelEl = drawer.querySelector ? drawer.querySelector('.challenge-drawer-panel') : null;
  var desired = null; // the user's chosen width; null = the default
  var lastApplied = 0; // the width apply() last wrote — the only size available while CLOSED
  return {
    axis: 'x',
    // The drawer is anchored to the pane's RIGHT edge: it grows LEFTWARD, and the separator
    // sits on its code-facing LEFT edge. `side` drives the drag anchor and the keyboard sign.
    side: 'right',
    key: DRAWER_KEY,
    handle: handle,
    label: 'wide',
    get desired() { return desired; },
    set desired(v) { desired = v; },
    bounds: function () { return drawerBounds(width(pane)); },
    // While CLOSED the panel is `display: none`, so its measured width is 0. Falling straight
    // through to the default would publish a wrong aria-valuenow for a student whose stored
    // width is not the default (reclamp syncs ARIA on resize even while closed), so remember
    // the width apply() last wrote.
    size: function () { return width(panelEl) || lastApplied || DRAWER_DEFAULT_PX; },
    // The CSS default lives in the template (`--drawer-w` on .challenge-drawer); an inline value
    // is set only while it differs from what the CSS would give, so a narrow pane still clamps.
    apply: function () {
      var px = clampDrawerWidth({ desired: desired, paneWidth: width(pane) });
      if (px === null) { drawer.style.removeProperty('--drawer-w'); return 0; }
      drawer.style.setProperty('--drawer-w', px + 'px');
      lastApplied = px;
      return px;
    },
    // The panel's RIGHT edge (the anchored one) — the drawer extends leftward from it.
    edgeFor: function () { return panelEl ? panelEl.getBoundingClientRect().right : 0; }
  };
}

function bannerPanel(doc, relayout) {
  var banner = doc.getElementById('instructions-banner');
  var bodyEl = doc.getElementById('instructions-banner-body');
  var handle = doc.getElementById('instructions-resize');
  var split = doc.querySelector('.editor-split');
  if (!banner || !bodyEl) { return null; }
  var desired = null;
  function measure() {
    var view = doc.defaultView;
    return {
      viewport: (view && view.innerHeight) || 0,
      content: bodyEl.scrollHeight || 0,
      current: height(bodyEl),
      split: height(split)
    };
  }
  return {
    axis: 'y',
    // The banner grows DOWNWARD from its top edge (edgeFor), like a left-anchored panel.
    side: 'left',
    key: BANNER_KEY,
    handle: handle,
    label: 'tall',
    get desired() { return desired; },
    set desired(v) { desired = v; },
    bounds: function () { return bannerBounds(measure()); },
    size: function () { return height(bodyEl); },
    apply: function () {
      if (desired === null || banner.classList.contains('is-collapsed')) {
        var had = banner.classList.contains('is-resized');
        banner.classList.remove('is-resized');
        banner.style.removeProperty('--ib-h');
        if (had && relayout) { relayout(); }
        return 0;
      }
      var m = measure();
      m.desired = desired;
      var px = clampBannerHeight(m);
      if (px === null) { return 0; }
      banner.style.setProperty('--ib-h', px + 'px');
      banner.classList.add('is-resized');
      if (relayout) { relayout(); }
      return px;
    },
    edgeFor: function () { return bodyEl.getBoundingClientRect().top; }
  };
}

function syncAria(p) {
  try {
    if (!p.handle) { return; }
    var b = p.bounds();
    var now = Math.round(p.size());
    if (b) {
      p.handle.setAttribute('aria-valuemin', String(Math.round(b.min)));
      p.handle.setAttribute('aria-valuemax', String(Math.round(b.max)));
    }
    p.handle.setAttribute('aria-valuenow', String(now));
    p.handle.setAttribute('aria-valuetext', p.desired === null
      ? 'Default size'
      : now + ' pixels ' + p.label);
  } catch (e) { /* never throw out of a listener */ }
}

function wireHandle(doc, p) {
  var handle = p.handle;
  if (!handle) { return; }
  // Shipped hidden + aria-hidden so it is never a focusable separator that resizes nothing;
  // BOTH come off together (a focusable aria-hidden node is an axe violation).
  handle.hidden = false;
  handle.removeAttribute('aria-hidden');

  var commit = function (px, persist) {
    var b = p.bounds();
    if (!b) { return; }
    p.desired = Math.round(Math.min(Math.max(num(px), b.min), b.max));
    var applied = p.apply();
    if (applied > 0) { p.desired = applied; }
    syncAria(p);
    if (persist && applied > 0) { writeStoredPx(doc, p.key, applied); }
  };
  var reset = function () {
    p.desired = null;
    p.apply();
    clearStored(doc, p.key);
    syncAria(p);
  };

  var dragging = false;
  var grab = 0;
  handle.addEventListener('pointerdown', function (e) {
    if (e.button !== undefined && e.button !== 0) { return; }
    // Grab offset: where the pointer is relative to the edge the panel grows AWAY from, so the
    // first move does not jolt the panel onto the pointer. A right-edge drawer grows leftward
    // from its RIGHT edge (anchor = edge - size); the banner grows down from its top edge
    // (anchor = edge + size).
    var size = p.size();
    var edge = p.edgeFor();
    var pos = p.axis === 'x' ? e.clientX : e.clientY;
    grab = pos - (p.side === 'right' ? edge - size : edge + size);
    dragging = true;
    try { handle.setPointerCapture(e.pointerId); } catch (err) { /* optional */ }
    try { handle.focus(); } catch (err) { /* optional */ }
    doc.body.classList.add('panel-dragging');
  });
  handle.addEventListener('pointermove', function (e) {
    if (!dragging) { return; }
    if (e.preventDefault) { e.preventDefault(); }
    var pos = p.axis === 'x' ? e.clientX : e.clientY;
    // The pinned algebra (A4): the moving edge is the panel's LEFT edge for a right drawer, so
    // the width is measured from the anchored edge, not by a sign flip of the left-drawer form.
    var next = p.side === 'right' ? p.edgeFor() - (pos - grab) : (pos - grab) - p.edgeFor();
    commit(next, false);
  });
  var end = function () {
    if (!dragging) { return; }
    dragging = false;
    doc.body.classList.remove('panel-dragging');
    if (p.desired !== null) { writeStoredPx(doc, p.key, p.desired); }
  };
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);

  handle.addEventListener('keydown', function (e) {
    var act = keyAction(e.key, p.axis);
    if (act === null) { return; }
    if (e.preventDefault) { e.preventDefault(); }
    if (act === 'reset') { reset(); return; }
    var b = p.bounds();
    if (!b) { return; }
    // The sign is inverted ONLY here, in the NUMERIC branch: a right-edge drawer grows when the
    // key points LEFT. `act` must never be negated before the 'min'/'max'/'reset' comparisons —
    // those are strings, and -act is NaN, which would silently commit min for Enter/End.
    var next = act === 'min' ? b.min : act === 'max' ? b.max
      : p.size() + (p.side === 'right' ? -act : act) * KEY_STEP_PX;
    commit(next, true);
  });
  handle.addEventListener('dblclick', function (e) {
    if (e.preventDefault) { e.preventDefault(); }
    reset();
  });
  syncAria(p);
}

// The panels the late wiring built, re-clamped on viewport / collapse changes.
var panels = [];

function build(doc, relayout) {
  var list = [];
  var d = drawerPanel(doc);
  if (d) { list.push(d); }
  var bn = bannerPanel(doc, relayout);
  if (bn) { list.push(bn); }
  return list;
}

// EAGER: replay the stored sizes.
export function applyStoredChallengePanels(doc) {
  try {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!isChallenge(doc)) { return; }
    build(doc, null).forEach(function (p) {
      p.desired = readStoredPx(doc, p.key);
      if (p.desired !== null) { p.apply(); }
    });
  } catch (e) { console.error(e); }
}

// LATE: the separators. `core` is optional (used only to re-lay-out Ace).
export function wireChallengePanelResize(core, doc) {
  try {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!isChallenge(doc)) { return; }
    var relayout = function () {
      try {
        var ed = core && core.getEditor && core.getEditor();
        if (ed && ed.resize) { ed.resize(); }
      } catch (e) { /* Ace is optional here */ }
    };
    panels = build(doc, relayout);
    panels.forEach(function (p) {
      p.desired = readStoredPx(doc, p.key);
      // Always applied here: with no stored size the drawer still clamps its CSS default to a
      // narrow pane, and the banner apply is a no-op that leaves the CSS default alone.
      p.apply();
      wireHandle(doc, p);
    });

    // A viewport change re-clamps (never persisted), as does collapsing / expanding the banner
    // (the base template's toggle flips `.is-collapsed`; a later re-open must re-apply).
    var view = doc.defaultView;
    var pending = false;
    var reclamp = function () {
      if (pending) { return; }
      pending = true;
      var run = function () {
        pending = false;
        panels.forEach(function (p) { try { p.apply(); syncAria(p); } catch (e) { /* keep going */ } });
      };
      if (view && view.requestAnimationFrame) { view.requestAnimationFrame(run); } else { setTimeout(run, 16); }
    };
    if (view && view.addEventListener) {
      view.addEventListener('resize', reclamp);
      view.addEventListener('orientationchange', reclamp);
    }
    var toggle = doc.getElementById('instructions-banner-toggle');
    if (toggle && toggle.addEventListener) { toggle.addEventListener('click', reclamp); }
    // The drawer opens / closes from challenge-chrome.js; its tab is the one control.
    var tab = doc.getElementById('challenge-drawer-tab');
    if (tab && tab.addEventListener) { tab.addEventListener('click', reclamp); }
  } catch (e) {
    console.error(e);
  }
}

// Test seam: forget module state between specs.
export function __resetChallengePanels() { panels = []; }
